// frontend/src/components/ReviewPage/filterApplicability.ts
//
// Row 23: structural filter-applicability over get_controls.dims_view (the full dim-combinations
// MV, delivered per fact). Mirrors api_internal._build_dim_filter_clauses: a row satisfies the
// filters iff for EVERY active filter dim D, row.dims[D] is defined and ∈ values. Raw-token
// equality; 'any' is an ordinary value. D2 COUPLING NOTE: if the walk's filter semantics ever
// change (e.g. 'any' becomes a wildcard), this predicate must change with them.
//
// v1 limitations (spec §3.1): value_type ignored (union across vts); multi-group-by uses
// groupBy[0] only; kpi matched against dims_view.kpi_type EXACTLY (zero matches → 'unknown',
// renders nothing — live-verified per fact in the walkthrough).
// Joint-attribution gap: a group needing relaxation of two+ dims appears in 'dropped' but not
// 'byDim' — consumers render such leftovers via the generic (unattributed) note line.
import type { DimsViewRow } from '~/api/endpoints/getControls'

export type FilterApplicability =
  | { status: 'unknown' }
  | { status: 'ok' }
  | {
      status: 'partial'
      groupDim: string
      expectedCount: number
      dropped: string[]
      byDim: Record<string, string[]>
    }
  | { status: 'empty'; inapplicableDims: string[] }

export interface FilterApplicabilityArgs {
  dimsView: DimsViewRow[]
  kpi: string
  groupBy: string[]
  filters: Record<string, string[]>
}

export function computeFilterApplicability(args: FilterApplicabilityArgs): FilterApplicability {
  const activeFilters = Object.entries(args.filters).filter(([, vs]) => vs.length > 0)
  if (activeFilters.length === 0) return { status: 'ok' }

  const rows = args.dimsView.filter((r) => r.kpi_type === args.kpi)
  if (rows.length === 0) return { status: 'unknown' }

  const matches = (r: DimsViewRow, dim: string, values: string[]): boolean => {
    const v = r.dims?.[dim]
    return v !== undefined && values.includes(v)
  }
  const satisfies = (r: DimsViewRow, omitDim?: string): boolean =>
    activeFilters.every(([dim, values]) => dim === omitDim || matches(r, dim, values))

  const surviving = rows.filter((r) => satisfies(r))
  if (surviving.length === 0) {
    const inapplicableDims = activeFilters
      .filter(([dim, values]) => !rows.some((r) => matches(r, dim, values)))
      .map(([dim]) => dim)
    return { status: 'empty', inapplicableDims }
  }

  const groupDim = args.groupBy[0]
  if (!groupDim) return { status: 'ok' }

  const distinctGroups = (rs: DimsViewRow[]): Set<string> => {
    const out = new Set<string>()
    for (const r of rs) {
      const g = r.dims?.[groupDim]
      if (g !== undefined) out.add(g)
    }
    return out
  }

  // A filter on the group dim itself narrows the user's selection — condition `expected`
  // on it so deselected groups are never reported as droppage (spec §3.1).
  const groupDimValues = args.filters[groupDim]
  const baseRows =
    groupDimValues && groupDimValues.length > 0
      ? rows.filter((r) => matches(r, groupDim, groupDimValues))
      : rows
  const expected = distinctGroups(baseRows)
  const survivingGroups = distinctGroups(surviving)
  const dropped = [...expected].filter((g) => !survivingGroups.has(g)).sort()
  if (dropped.length === 0) return { status: 'ok' }

  // Attribution: D readmits group g when relaxing D alone lets a row with group g pass.
  // The group dim itself is never an offender (its filter IS the selection).
  const byDim: Record<string, string[]> = {}
  for (const [dim] of activeFilters) {
    if (dim === groupDim) continue
    const readmitted = [...distinctGroups(baseRows.filter((r) => satisfies(r, dim)))]
      .filter((g) => !survivingGroups.has(g))
      .sort()
    if (readmitted.length > 0) byDim[dim] = readmitted
  }
  return { status: 'partial', groupDim, expectedCount: expected.size, dropped, byDim }
}
